<script setup>
import { VueFlow, useVueFlow, Position } from "@vue-flow/core";
import { Background } from "@vue-flow/background";
import { nextTick, watch, ref, provide, onBeforeUnmount } from "vue";
import {
  fetchDetailsOperator,
  setNodeId,
  containsNode,
  findNodesByType,
  validate,
  validateConnection,
} from "../mixins/VueFlowEditor.js";

import { useI18n } from "vue-i18n";
import SidebarOperators from "./SidebarOperators.vue";
import NodeDefault from "./NodeDefault.vue";
import NodeInOut from "./NodeInOut.vue";
import InteractionControls from "./InteractionControls.vue";
import EdgeCustom from "./EdgeCustom.vue";
import DialogOperator from "./DialogOperator.vue";
import DialogPreview from "./DialogPreview.vue";
import DialogDebug from "./DialogDebug.vue";

const { t } = useI18n();
const props = defineProps(["api", "apiDraw", "apiChat", "realm"]);

let nodeDetails = ref(null);
let showSidebar = ref("sidebar");
let snackbar = ref(true);
let snacktext = ref(t("strings.default-message-snackbar"));
let snackcolor = ref("green");
let snacktimeout = ref(2000);
let showDialogOperator = ref(false);
let showDialogPreview = ref(false);
let showDialogDebug = ref(false);
let sourceNode = ref(null);
let targetNode = ref(null);
let previewTargetLabel = ref(null);
let previewTargetId = ref(null);
let debugData = ref(null);

const {
  nodes,
  findNode,
  onConnect,
  addEdges,
  addNodes,
  project,
  vueFlowRef,
  toObject,
  updateNode,
} = useVueFlow();

onBeforeUnmount(() => {
  sessionStorage.removeItem("mashup");
  sessionStorage.removeItem("mashupDebug");
});

// This function is called when a draggable element or text selection is dragged over a valid drop target.
function onDragOver(event) {
  // Prevents the default behavior of the event to allow a drop.
  event.preventDefault();
  // Checks if a dataTransfer object exists in the event.
  if (event.dataTransfer) {
    // Sets the drop effect to "move", indicating that the dragged item will be moved to the drop target upon release.
    event.dataTransfer.dropEffect = "move";
  }
}

async function onDrop(event) {
  try {
    if (
      event.target.className === "vue-flow__pane vue-flow__container draggable"
    ) {
      event.preventDefault();
      event.stopPropagation();
      const node = JSON.parse(event.dataTransfer?.getData("application/json"));
      const { left, top } = vueFlowRef.value.getBoundingClientRect();

      const position = project({
        x: event.clientX - left,
        y: event.clientY - top,
      });
      const idRoot = node.category.id === "inputNode" ? "in" : "op";
      nodeDetails.value = await fetchDetailsOperator(
        node.category.id,
        node.item.id,
        props.apiDraw,
        props.realm,
      );
      if (nodeDetails.value) {
        nodeDetails.value["inputs"] = [];
        nodeDetails.value["outputs"] = [];
        nodeDetails.value["label"] =
          `${node.item.name}_${setNodeId(nodes.value)}`;
      }
      const newNode = {
        id: `${idRoot}_${setNodeId(nodes.value)}`,
        type: node.category.id,
        operatorId: node.item.id,
        position,
        label: node.item.name,
        data: {
          toolbarPosition: Position.Top,
          icon: node.item.icon,
          color: node.category.color,
          subCategory: node.subCategory[0], // Added subCategory ID to node
          subCategoryName: node.subCategory[1], // Added subCategory Name to node
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        userData: nodeDetails.value,
      };
      addNodes([newNode]);
      //Align node position after drop, so it's centered to the mouse
      nextTick(() => {
        const node = findNode(newNode.id);
        const stop = watch(
          () => node.dimensions,
          (dimensions) => {
            if (dimensions.width > 0 && dimensions.height > 0) {
              node.position = {
                x: node.position.x - node.dimensions.width / 2,
                y: node.position.y - node.dimensions.height / 2,
              };
              stop();
            }
          },
          { deep: true, flush: "post" },
        );
      });
    } else {
      throw Error();
    }
  } catch (error) {
    console.error("Error, you dropped it on wrong location: ", error);
  }
} //end OnDrop()

//EDGES//
/**
 * Manages open/close dialog operator
 * Toggles the visibility of the dialog operator based on the provided value and nodeId.
 *
 * @param {boolean} value - The desired visibility state of the dialog.
 * @param {string|null} nodeId - The ID of the node to be targeted.
 */
function toggleDialogOperator(value, nodeId) {
  // Close the dialog if value is false and nodeId is null
  if (value === false && nodeId === null) {
    showDialogOperator.value = false;
    targetNode.value = null;
  }
  // Open the dialog for a new node if targetNode is not set and nodeId is provided
  else if (!targetNode.value && nodeId) {
    targetNode.value = findNode(nodeId);
    showDialogOperator.value = true;
  }
  // Change the current node and reopen the dialog if the nodeId is different and the dialog is open
  else if (
    targetNode.value?.id !== nodeId &&
    showDialogOperator.value === true
  ) {
    showDialogOperator.value = false;
    setTimeout(() => {
      targetNode.value = findNode(nodeId);
      showDialogOperator.value = value;
    }, 0);
  }
  // Update the dialog state if targetNode is already set
  else if (targetNode.value) {
    targetNode.value = findNode(nodeId); // Ensure targetNode is set correctly
    showDialogOperator.value = value;
  }
}

//Manages nodes connections
onConnect((params) => {
  if (!validateConnection(params)) {
    return;
  }
  showDialogOperator.value = false;

  sourceNode.value = findNode(params.source);
  targetNode.value = findNode(params.target);

  addEdges([
    {
      id: `${params.source}-->${params.target}`,
      type: "custom-edge",
      operatorId: "vme2.shape.Connection",
      userData: { sourceId: params.source, targetId: params.target },
      data: { label: getEdgeLabel(params.sourceHandle) },
      ...params,
    },
  ]);

  const showNodeSettings = JSON.parse(
    localStorage.getItem("flowSettings"),
  ).showNodeSettings;

  // Initialize node inputs and outputs array if userData doesn't exist
  if (!targetNode.value.userData) {
    targetNode.value.userData = { inputs: [], outputs: [] };
  }
  if (!sourceNode.value.userData) {
    sourceNode.value.userData = { inputs: [], outputs: [] };
  }

  // Inserts linked Source inside Target node if it doesn't already contain it
  if (!containsNode(targetNode, sourceNode.value.id)) {
    targetNode.value.userData.inputs?.push(sourceNode.value.id);
    sourceNode.value.userData.outputs?.push(targetNode.value.id);

    if (sourceNode.value.userData.idprefix == "ifelse") {
      updateSourceNode(
        sourceNode.value.userData.parameters,
        params.sourceHandle,
        targetNode.value.id,
      );
    }
    if (params.target !== "output") {
      // Open bottom-sheets for settings
      showDialogOperator.value = showNodeSettings;
    } else {
      showDialogOperator.value = false;
      sourceNode.value = null;
      targetNode.value = null;
    }
  }
});

/**
 * Returns the label for an edge connecting an if-else node to another node.
 * If the handle label contains "-else", it returns "false".
 * If the handle label contains "-if", it returns "true".
 * Otherwise, it returns null.
 */
function getEdgeLabel(handleLabel) {
  return handleLabel.includes("[else]")
    ? "false"
    : handleLabel.includes("[if]")
      ? "true"
      : null;
}

/**
 * Updates the source node's parameters based on the extracted condition from the handle label.
 * Finds the appropriate object in the parameters array and sets its 'property' key to the given value.
 *
 * @param {Array} array - The array of parameters.
 * @param {String} condition - The handle label containing the condition.
 * @param {String} propertyValue - The value to set for the 'property' key.
 */
function updateSourceNode(array, condition, propertyValue) {
  // Extract the value between square brackets e.g., right-handle-op_1[else]
  let extractedCondition = condition.match(/\[(.*?)\]/)[1];
  // Find the appropriate object in 'parameters' based on the extracted value
  let targetObject = array.find(
    (obj) => obj.name === (extractedCondition === "if" ? "iftrue" : "iffalse"),
  );
  // Add the 'property' key with the passed value
  if (targetObject) {
    targetObject.property = propertyValue;
  }
}

//Saves mashup on sessionStorage
function onSave(userData) {
  if (userData) {
    if (Object.keys(userData).length <= 1) {
      targetNode.value["userData"]["parameters"] = [...userData];
    } else {
      targetNode.value["userData"] = { ...userData };
    }
    // Force reactive update using updateNode with a new object reference
    updateNode(targetNode.value.id, {
      userData: { ...targetNode.value.userData },
    });
    try {
      sessionStorage.setItem("mashup", JSON.stringify(toObject()));
      setSnackbar(true, "strings.save-complete", "green", 2000);
    } catch {
      setSnackbar(false, "strings.something-went-wrong", "red", 5000);
    }
  }
}

//Finds a whole node object by its id
function findNodeById(id) {
  return findNode(id);
}
//Provides the findNodeById function to descendant components.
provide("findNodeById", findNodeById);

// Sets necessary params for the snackbar of the editor
// The snackbar text could be also multi-string with separator '#' chars for each string
function setSnackbar(
  value = snackbar.value,
  text = snacktext.value,
  color = snackcolor.value,
  timeout = snacktimeout.value,
) {
  snackbar.value = value;
  // Split the text and map it to the correct format
  const formattedText = text
    .split("#")
    .map((txt) => {
      // It returns the string or the translated locale string
      return !txt.includes("strings.") ? txt : t(txt);
    })
    .join("\n"); // Join the formatted text with newline characters
  // Set the snacktext value to the formatted text
  snacktext.value = formattedText;
  snackcolor.value = color;
  snacktimeout.value = timeout;
}

function toggleSidebar(value) {
  showSidebar.value = value;
}
//Open-Close dialog Preview Mashup
function toggleDialogPreview(value) {
  showDialogPreview.value = value;
}
function mashupPreview(value, targetLabel, targetId) {
  previewTargetLabel.value = targetLabel;
  previewTargetId.value = targetId;
  toggleDialogPreview(value);
}

// Asynchronous function to validate data
async function mashupValidate(debug) {
  sessionStorage.removeItem("mashupDebug");
  // // Execute the validation function and wait for the result
  if (!debug) debug = await validate(props.apiDraw, { draw: toObject() });
  // Store the validation result in the session
  sessionStorage.setItem("mashupDebug", JSON.stringify(debug));
  // If there are errors
  if (debug.errorCounter > 0) {
    // Shows a snackbar with the error message;
    setSnackbar(true, "strings.validate-alert", "red", 3000);
  } else {
    // Shows a snackbar with the success message;
    setSnackbar(true, "strings.default-message-snackbar", "blue", 2000);
  }
  if (debug.mainExec && typeof debug.mainExec === "object") {
    const mainExecKeys = Object.keys(debug.mainExec);
    // For each mainExec key inside debug object
    mainExecKeys.forEach((nodeId) => {
      // Update nodes with errors found for each key
      updateNodes(nodeId, debug.mainExec[nodeId].errors);
    });
  }
}
// Asynchronous function to validate data
async function mashupDebugExecution(preview) {
  const debug =
    preview.debug || JSON.parse(sessionStorage.getItem("mashupDebug"));
  sessionStorage.removeItem("mashupDebug");
  // // Execute the validation function and wait for the result
  if (debug) sessionStorage.setItem("mashupDebug", JSON.stringify(debug));
  // If there are errors
  if (debug.errorCounter > 0) {
    // Shows a snackbar with the error message;
    setSnackbar(true, "strings.excution-alert", "red", 3000);
    toggleDialogPreview(false);
  } else {
    // Shows a snackbar with the success message;
    setSnackbar(true, "strings.default-message-snackbar", "blue", 2000);
  }
  if (debug.mainExec && typeof debug.mainExec === "object") {
    const mainExecKeys = Object.keys(debug.mainExec);
    // For each mainExec key inside debug object
    mainExecKeys.forEach((nodeId) => {
      // Update nodes with errors found for each key
      updateNodes(nodeId, debug.mainExec[nodeId].errors);
    });
  }
}

// Function to update a specific node with new data
function updateNodes(nodeId, newData) {
  // Find the corresponding node
  const nodeToUpdate = findNode(nodeId);
  // If the node exists
  if (nodeToUpdate) {
    // Update the node's data
    nodeToUpdate.data.errors = newData;
    // Update the node using the VueFlow updateNode function
    updateNode(nodeId, nodeToUpdate);
  }
}

//Open-Close dialog Debug Mashup
function toggleDialogDebug(value, id, label, data) {
  debugData.value = { id: id, label: label, data: data };
  showDialogDebug.value = value;
}
</script>

<template>
  <div class="container_flow" @drop="onDrop">
    <SidebarOperators
      :tenantId="props.realm"
      :apiDraw="props.apiDraw"
      @showSidebar="toggleSidebar"
    />

    <VueFlow
      class="default_flow"
      :class="showSidebar"
      :default-edge-options="{ type: 'custom-edge' }"
      @dragover="onDragOver"
    >
      <!-- Nodes -->
      <template #node-inputNode="nodeProps">
        <NodeInOut
          :nodeInfo="nodeProps"
          @preview="mashupPreview"
          @toggleDialogOperator="toggleDialogOperator"
          @toggleDialogDebug="toggleDialogDebug"
        />
      </template>
      <template #node-outputNode="nodeProps">
        <NodeInOut :nodeInfo="nodeProps" />
      </template>
      <template #node-basic="nodeProps">
        <NodeDefault
          :nodeInfo="nodeProps"
          @toggleDialogOperator="toggleDialogOperator"
          @preview="mashupPreview"
          @toggleDialogDebug="toggleDialogDebug"
        />
      </template>
      <template #node-fiware_datamodel="nodeProps">
        <NodeDefault
          :nodeInfo="nodeProps"
          @toggleDialogOperator="toggleDialogOperator"
          @preview="mashupPreview"
          @toggleDialogDebug="toggleDialogDebug"
        />
      </template>
      <template #node-fhir_datamodel="nodeProps">
        <NodeDefault
          :nodeInfo="nodeProps"
          @toggleDialogOperator="toggleDialogOperator"
          @preview="mashupPreview"
          @toggleDialogDebug="toggleDialogDebug"
        />
      </template>
      <template #node-remoteConnectors="nodeProps">
        <NodeDefault
          :nodeInfo="nodeProps"
          @toggleDialogOperator="toggleDialogOperator"
          @preview="mashupPreview"
          @toggleDialogDebug="toggleDialogDebug"
        />
      </template>
      <template #node-customOperators="nodeProps">
        <NodeDefault
          :nodeInfo="nodeProps"
          @toggleDialogOperator="toggleDialogOperator"
          @preview="mashupPreview"
          @toggleDialogDebug="toggleDialogDebug"
        />
      </template>
      <template #node-dataSources="nodeProps">
        <NodeDefault
          :nodeInfo="nodeProps"
          @toggleDialogOperator="toggleDialogOperator"
          @preview="mashupPreview"
          @toggleDialogDebug="toggleDialogDebug"
        />
      </template>
      <!-- Edges -->
      <template #edge-custom-edge="props">
        <EdgeCustom v-bind="props" />
      </template>
      <!-- Editor background -->
      <Background pattern-color="#aaa" gap="10" variant="dots" />
      <!-- Editor toolbar -->
      <InteractionControls
        :tenantId="props.realm"
        :api="props.api"
        :apiDraw="props.apiDraw"
        :apiChat="props.apiChat"
        @setSnackbar="setSnackbar"
        @preview="mashupPreview"
        @validate="mashupValidate"
      />
      <!-- Operator Dialog settings -->
      <DialogOperator
        v-if="showDialogOperator"
        :showDialogOperator="showDialogOperator"
        :target="targetNode"
        :apiDraw="props.apiDraw"
        @toggleDialogOperator="toggleDialogOperator"
        @saveFlow="onSave"
      ></DialogOperator>
      <!-- Operator Dialog Preview -->
      <DialogPreview
        v-if="showDialogPreview"
        :showDialogPreview="showDialogPreview"
        :targetLabel="previewTargetLabel"
        :targetId="previewTargetId"
        :nodes="findNodesByType(nodes, 'inputNode')"
        :apiDraw="props.apiDraw"
        :canvaNodes="toObject()"
        @toggleDialogPreview="toggleDialogPreview"
        @debugExecution="mashupDebugExecution"
        @setSnackbar="setSnackbar"
      ></DialogPreview>
      <!-- Operator Dialog Debug -->
      <DialogDebug
        v-if="showDialogDebug"
        :showDialogDebug="showDialogDebug"
        :debugData="debugData"
        @toggleDialogDebug="toggleDialogDebug"
      ></DialogDebug>
      <!-- SNACKBAR -->
      <v-snackbar
        v-model="snackbar"
        :color="snackcolor"
        :timeout="snacktimeout"
        position="absolute"
        :absolute="true"
        :multi-line="true"
        style="white-space: pre-line"
      >
        {{ snacktext }}
        <template v-slot:actions>
          <v-btn variant="text" @click="snackbar = false">
            {{ $t("labels.close") }}
          </v-btn>
        </template>
      </v-snackbar>
    </VueFlow>
  </div>
</template>

<style scoped>
/**vue-flow*/
main .vue-flow {
  top: 64px;
}
main.expanded .vue-flow {
  width: calc(100% - 320px);
  height: calc(100% - 104px);
}

main.reduced .vue-flow {
  width: calc(100% - 320px);
  height: calc(100% - 104px);
}

main.expanded .vue-flow.sidebar {
  width: calc(100% - 320px);
}

main.expanded .vue-flow.no-sidebar {
  width: calc(100% - 320px + 319px);
}

main.reduced .vue-flow.sidebar {
  width: calc(100% - 320px);
}

main.reduced .vue-flow.no-sidebar {
  width: calc(100% - 320px + 319px);
}

.container_flow {
  flex-direction: column;
  display: flex;
  height: 100vh;
  border-top: 1px solid #e0e0e0;
}

.container_flow .vue-flow-wrapper {
  flex-grow: 1;
  height: 100%;
}

.vue-flow__minimap {
  transform: scale(75%);
  transform-origin: bottom right;
}

.default_flow.dark {
  background: #57534e;
  color: #fffffb;
}

.default_flow.dark .vue-flow__node {
  background: #292524;
  color: #fffffb;
}

.default_flow.dark .vue-flow__controls .vue-flow__controls-button {
  background: #292524;
  fill: #fffffb;
  border-color: #fffffb;
}

.default_flow.dark .vue-flow__edge-textbg {
  fill: #292524;
}

.default_flow.dark .vue-flow__edge-text {
  fill: #fffffb;
}

.default_flow .controls {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.default_flow .controls button {
  padding: 4px;
  border-radius: 5px;
  font-weight: 600;
  -webkit-box-shadow: 0px 5px 10px 0px rgba(0, 0, 0, 0.3);
  box-shadow: 0 5px 10px #0000004d;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
}

.default_flow .controls button:hover {
  transform: scale(102%);
  transition: 0.25s all ease;
}

/***/

@media screen and (min-width: 640px) {
  .container_flow {
    flex-direction: row;
  }
}
</style>
